import React, { useMemo } from 'react';
import { Button, Card, Empty, Select, Typography } from 'antd';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TAIModelBuilderDatasetEntry, TAIModelBuilderLessonConfig } from '../../../models/courses/aiModelBuilder';
import '../student/AIModelBuilderLessonPlayer.css';

type LanguageKey = 'fr' | 'en' | 'ar';

type ModelBuilderItem = {
  id: string;
  label: string;
  expectedKey: string | null;
  isNoise: boolean;
};

type ModelBuilderTarget = {
  id: string;
  label: string;
};

interface AIModelBuilderLessonTeacherProps {
  config: TAIModelBuilderLessonConfig;
  statement?: string;
}

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const getFeaturesByLanguage = (entry: TAIModelBuilderDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const getNoiseByLanguage = (config: TAIModelBuilderLessonConfig, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
    case 'ar':
      return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
    default:
      return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
  }
};

const AIModelBuilderLessonTeacher: React.FC<AIModelBuilderLessonTeacherProps> = ({ config, statement }) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');

  const allNoiseItems = useMemo(() => getNoiseByLanguage(config, language), [config, language]);

  const { targets, assignments, noiseBin } = useMemo(() => {
    const entries = Object.entries(config.dataset || {});
    const builtTargets: ModelBuilderTarget[] = entries.map(([id, entry]) => ({ id, label: entry.key || id }));

    const assignments: Record<string, ModelBuilderItem[]> = {};

    entries.forEach(([id, entry]) => {
      const features = getFeaturesByLanguage(entry, language);
      const items: ModelBuilderItem[] = features.map((feature, index) => ({
        id: `${id}-feature-${index}`,
        label: feature,
        expectedKey: id,
        isNoise: false,
      }));

      assignments[id] = items;
    });

    const noiseItems: ModelBuilderItem[] = allNoiseItems.map((noise, index) => ({
      id: `noise-${index}`,
      label: noise,
      expectedKey: null,
      isNoise: true,
    }));

    return {
      targets: builtTargets,
      assignments,
      noiseBin: noiseItems,
    };
  }, [config.dataset, allNoiseItems, language]);

  const isImagePath = (v: string) => /\.(png|jpe?g|gif|svg|webp)(\?|#|$)/i.test(v) || v.includes('/images/') || v.includes('/uploads/');
  const isAudioPath = (v: string) => /\.(mp3|wav|ogg|m4a)(\?|#|$)/i.test(v) || v.includes('/sounds/') || v.includes('/audio/');

  const renderItem = (item: ModelBuilderItem, index: number) => (
    <div key={item.id} className="aimb-draggable" style={{ cursor: 'default' }}>
      {isImagePath(item.label) ? (
        <img src={getImagePath(item.label)} alt="feature" className="aimb-draggable-media" />
      ) : isAudioPath(item.label) ? (
        <audio src={getSoundPath(item.label)} controls className="aimb-draggable-media" />
      ) : (
        <Typography.Text ellipsis={{ tooltip: item.label }}>
          {item.label}
        </Typography.Text>
      )}
    </div>
  );

  const renderDropZone = (droppableId: string, title: string, items: ModelBuilderItem[]) => {
    const showImage = !!title && isImagePath(title);
    const showAudio = !!title && isAudioPath(title);
    const isTrashBin = droppableId === 'noise-bin';
    return (
      <Card
        title={
          isTrashBin ? (
            <div className="aimb-trash-title">
              <DeleteOutlined className="aimb-trash-icon" />
              <span>{title}</span>
            </div>
          ) : showImage || showAudio ? (
            undefined
          ) : (
            title
          )
        }
        className={`aimb-dropzone ${isTrashBin ? 'aimb-dropzone--trash' : ''}`}
      >
        {showImage && (
          <div className="aimb-media-preview">
            <img src={getImagePath(title)} alt="key" />
          </div>
        )}
        {showAudio && (
          <div className="aimb-media-preview">
            <audio src={getSoundPath(title)} controls />
          </div>
        )}
        <div className="aimb-dropzone-content">
          {items.length === 0 ? (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={isTrashBin ? t('aiModelBuilder.student.emptyTrash') : undefined}
            />
          ) : (
            items.map((item, index) => renderItem(item, index))
          )}
        </div>
      </Card>
    );
  };

  const availableDataTypes = useMemo(
    () => Object.entries(config.data_type || {}),
    [config.data_type],
  );

  const firstActiveDataType = useMemo(() => {
    const activeTypes = availableDataTypes.filter(([, value]) => (value.status ?? 'false') === 'true');
    return activeTypes.length > 0 ? activeTypes[0][0] : availableDataTypes[0]?.[0];
  }, [availableDataTypes]);

  if (!targets.length) {
    return <Empty description={t('aiModelBuilder.student.empty')} />;
  }

  return (
    <div className="aimb-player aimb-player--builder">
      {statement || config.statement ? (
        <div className="aimb-statement">
          {statement || config.statement}
        </div>
      ) : null}
      <div className="aimb-layout">
        <div className="aimb-column aimb-column--keys">
          <div className="aimb-keys-grid">
            {targets.map((target) => renderDropZone(target.id, target.label, assignments[target.id] || []))}
          </div>
        </div>
        <div className="aimb-column aimb-column--right">
          <div className="aimb-right-section">
            <div className="aimb-trash-wrapper">
              {renderDropZone('noise-bin', t('aiModelBuilder.student.noiseBin'), noiseBin)}
            </div>
            <div className="aimb-column aimb-column--controls">
              <Card className="aimb-control-card" title={t('aiModelBuilder.student.dataType')}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={t('aiModelBuilder.student.selectDataType')}
                  size="large"
                  options={availableDataTypes.map(([id, value]) => ({
                    value: id,
                    label: value[`title_${language}` as keyof typeof value] || value.title_en || id,
                  }))}
                  value={firstActiveDataType}
                  disabled
                />
              </Card>
              <Card className="aimb-control-card aimb-training-card">
                <div className="aimb-training-content">
                  <Button
                    block
                    size="large"
                    className="aimb-train-button"
                    disabled
                    style={{
                      background: 'linear-gradient(135deg, #2992bf, #8bbe1e)',
                      color: '#ffffff',
                      border: 'none',
                      opacity: 0.6,
                      fontWeight: 800,
                      fontSize: '18px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('aiModelBuilder.student.train')}
                  </Button>
                  <div className={`aimb-accuracy aimb-accuracy--success`}>
                    <Typography.Text strong>
                      {t('aiModelBuilder.student.accuracy', { value: 100 })}
                    </Typography.Text>
                    <Typography.Text type="success" className="aimb-success-message">
                      {t('aiModelBuilder.student.messages.trainingSuccess')}
                    </Typography.Text>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIModelBuilderLessonTeacher;
